// ─────────────────────────────────────────────
//  breakPieces — the mobile break visual language, shared by every screen
//  that renders break state (home cards, give-break flow, break board).
//
//  The look is the client-approved prototype, which itself mirrors the WEB
//  room card the client screenshotted: four pills (Brea/Lunc/Dinn/Bath), a
//  "Giving a break: NAME ✓" strip while covered, and a requested strip while
//  waiting for cover. The pill and the strip BLINK IN SYNC — both use the
//  same global `atcFlash` keyframe on the same clock (defined in index.js and
//  suppressed there under prefers-reduced-motion, like every web blink).
//
//  State colours never travel alone: each cell also carries a glyph
//  (✓ done · ◐ covering · ! requested · — none) and an aria-label, because
//  colour-only state was one of the two WCAG violations the design review
//  measured in the mockups.
//
//  Presentational only — no Firestore, no domain transitions. Callers hand in
//  a breaks record (domain/breaks shape) and the action callbacks.
// ─────────────────────────────────────────────
import React from "react";
import mobile, { px } from "../../theme/tokens.mobile";
import { BREAK_CHANNELS, getChannelState } from "../../domain/breaks";
// The audit FLOOR for a touch target, imported rather than restated — two
// copies of a minimum is how one of them quietly stops being 48.
import { TOUCH_TARGET_MIN } from "./roomStatusPieces";

const { color } = mobile;
const { space, radius, font } = mobile;

// The web room card's four-letter pill labels, exactly as the client's
// screenshots show them (Brea / Lunc / Dinn / Bath).
export const PILL_LABELS = {
  breakfast: "Brea",
  lunch: "Lunc",
  dinner: "Dinn",
  bathroom: "Bath",
};

// Long-form labels for aria and toasts.
export const CHANNEL_LABELS = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  bathroom: "Bathroom",
};

// Exported: MobileBreakBoard renders the same glyph/word pairs on its grid
// cells, and a drifted copy would blink one meaning on the card and another
// on the board.
export const STATE_GLYPHS = { done: "✓", in_progress: "◐", assigned: ">", requested: "!", none: "" };
export const STATE_WORDS = {
  done: "done",
  in_progress: "being covered",
  // ⚠️ THE SUBJECT IS THE COVER, NOT THE PERSON ON BREAK. Every other word here
  // describes the provider whose card this is; this one describes somebody
  // else, so the caller passes the name in (see BreakPills).
  assigned: "cover assigned, not in the room yet",
  requested: "requested, waiting for cover",
  none: "not taken",
};

// ⚠️ NOT atcFlash. That keyframe drops the whole element to 25% opacity, and on
// the mobile app's dark navy card a red "requested" or amber "covering" pill at
// 25% is indistinguishable from an untaken one — for half of every second the
// board looked like nothing was happening, which is exactly the "colours are
// not showing on mobile" report.
//
// atcStatePulse blinks between the state colour and a DIMMER SHADE OF THE SAME
// COLOUR (passed per element as --atc-pulse-dim), so the pulse is just as
// noticeable but red still reads as red at every instant.
const pulse = (dim) => ({
  animation: "atcStatePulse 1s steps(1) infinite",
  "--atc-pulse-dim": dim,
});

// Dim partners for the three live states — same hue, clearly darker.
// "HH:MM" → whole minutes since, using the same wall clock the stamps use.
// Returns null when either end is missing or the clock has crossed midnight
// backwards — a negative age is a clock problem, and rendering it as "-1123m"
// is worse than rendering nothing.
export function elapsedMin(sinceHm, nowHmStr) {
  const parse = (v) => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(v || ""));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const a = parse(sinceHm);
  const b = parse(nowHmStr);
  if (a == null || b == null) return null;
  const d = b - a;
  return d >= 0 ? d : null;
}

export const PULSE_DIM = {
  requested: "#7f1d1d",   // deep red   (vs break.requested #ef4444)
  in_progress: "#8a5300", // deep amber (vs break.giving    #f59e0b)
};

function pillStyle(state) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: px(4),
    padding: `${px(5)} ${px(11)}`,
    borderRadius: radius.full,
    fontSize: 11,
    fontWeight: font.weight?.bold || 700,
    letterSpacing: "0.02em",
    border: `1px solid ${color.border.card}`,
    background: color.break.emptyCell,
    color: color.text.muted,
    minHeight: 28,
  };
  // YELLOW blinking = someone is covering the break right now.
  if (state === "in_progress")
    return { ...base, background: color.break.giving, borderColor: color.break.giving, color: color.break.glyphInk, ...pulse(PULSE_DIM.in_progress) };
  // GREEN solid = the break is finished. Never blinks: it is a settled fact,
  // and motion on it would compete with the two states that need attention.
  if (state === "done")
    return { ...base, background: color.break.done, borderColor: color.break.done, color: color.break.glyphInk };
  // HOLLOW with a CYAN RING = somebody has been asked and is on their way.
  //
  // ⚠️ THE ONLY HOLLOW LIVE STATE, and deliberately not a fill: on this dark
  // navy card the three saturated fills all mean "something is happening in
  // that room", and nothing is happening in this one yet. It is told apart from
  // `none` — which shares the fill — by the heavier ring, the hue, and by
  // carrying a glyph where `none` carries none.
  //
  // ⚠️ AND IT DOES NOT PULSE. `PULSE_DIM` gains no entry, so `pulseFor` returns
  // null for it. A pulse here would borrow the urgency of `requested`, which
  // means NOBODY has answered — an answered ask that blinks identically erases
  // the answer.
  if (state === "assigned")
    return { ...base, background: color.break.emptyCell, border: `2px solid ${color.accent?.cyan || "#45BBFF"}`, color: color.accent?.cyan || "#45BBFF" };
  // RED blinking = waiting for someone to cover.
  if (state === "requested")
    return { ...base, background: color.break.requested, borderColor: color.break.requested, color: "#fff", ...pulse(PULSE_DIM.requested) };
  return base;
}

// The four-pill row. Pure display of a breaks record.
export function BreakPills({ breaks, relieverName = "" }) {
  return (
    <div style={{ display: "flex", gap: px(6), flexWrap: "wrap", marginTop: px(space.sm) }} role="group" aria-label="Break status">
      {BREAK_CHANNELS.map((ch) => {
        const state = getChannelState(breaks, ch);
        return (
          <span
            key={ch}
            style={pillStyle(state)}
            // ⚠️ `role="img"`. This was a bare span with an aria-label, which is
            // not reliably announced at all — on the one device the coverer
            // actually carries, the break state was silently unavailable.
            role="img"
            aria-label={
              state === "assigned" && relieverName
                ? `${CHANNEL_LABELS[ch]}: assigned to ${relieverName}, not in the room yet`
                : `${CHANNEL_LABELS[ch]}: ${STATE_WORDS[state]}`
            }
          >
            {PILL_LABELS[ch]}
            {STATE_GLYPHS[state] && <span aria-hidden="true">{STATE_GLYPHS[state]}</span>}
          </span>
        );
      })}
    </div>
  );
}

// "⏳ NAME — not in the room yet · 6m" — a cover has been NAMED and has not
// arrived. The room is still uncovered and its provider still has their hands
// on the patient.
//
// ⚠️ NAVY, NOT AMBER, AND IT DOES NOT PULSE. Amber means "a reliever is standing
// in this room" everywhere else in ATC, and pulsing is how `requested` says
// nobody has answered. This state is the answered ask, and the room is still
// waiting — it has to be legible and calm. Escalation for a cover who never
// turns up belongs in NEEDS YOU NOW, which owns the alarm vocabulary; putting
// break noise in the alarm lane is how staff learn to ignore the alarm lane.
//
// The elapsed minutes are aria-hidden: the strip announces once when it
// appears, and a counter inside the live region would re-announce the whole
// line every sixty seconds.
export function AwaitingCoverStrip({ relieverName, sinceHm = null, nowHm: nowHm_ = null }) {
  if (!relieverName) return null;
  const mins = elapsedMin(sinceHm, nowHm_);
  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: px(space.sm),
        marginTop: px(space.sm),
        padding: `${px(7)} ${px(10)}`,
        borderRadius: radius.md,
        border: `1.5px solid ${color.accent?.cyan || "#45BBFF"}`,
        background: color.break.emptyCell,
        color: color.accent?.cyan || "#45BBFF",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      <span aria-hidden="true">⏳</span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <b>{relieverName}</b> — not in the room yet
      </span>
      {mins != null && (
        <span aria-hidden="true" style={{ flexShrink: 0, opacity: 0.85 }}>{mins}m</span>
      )}
    </div>
  );
}

// The arrival tap, shown ONLY to the person who was named.
//
// ⚠️ THE FIRST TAP NEVER WRITES. A sleeve brushing a control must not start a
// coverage clock — the whole point of the split is that the board's claim is
// true — so tap one discloses a confirm row and tap two commits.
//
// The label names the CLINICAL moment, not the walk: people skip a tap that
// means "I arrived" and do not skip one that means they took handoff. And no
// other control on these screens may share this verb — "Take over" already
// means taking the room outright, "End break" means finishing coverage.
export function ArrivedStrip({ channelLabel, roomName, onArrive, busy = false }) {
  const [confirming, setConfirming] = React.useState(false);
  const btn = {
    minHeight: 48,
    borderRadius: radius.md,
    border: `2px solid ${color.accent?.cyan || "#45BBFF"}`,
    background: color.accent?.primary || "#005CD6",
    color: "#fff",
    fontSize: 13,
    fontWeight: 800,
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.5 : 1,
    flex: 1,
    padding: `0 ${px(10)}`,
  };
  if (!confirming) {
    return (
      <div style={{ display: "flex", marginTop: px(space.sm) }}>
        <button type="button" style={btn} disabled={busy} onClick={() => setConfirming(true)}>
          Got report — I have the room
        </button>
      </div>
    );
  }
  return (
    <div
      role="group"
      aria-label={`Confirm you are in ${roomName || "the room"}`}
      style={{ display: "flex", gap: px(space.sm), marginTop: px(space.sm) }}
    >
      <button type="button" style={btn} disabled={busy} onClick={onArrive}>
        Yes — start {channelLabel || "the"} cover
      </button>
      <button
        type="button"
        style={{ ...btn, background: "transparent", color: color.accent?.cyan || "#45BBFF" }}
        disabled={busy}
        onClick={() => setConfirming(false)}
      >
        Not yet
      </button>
    </div>
  );
}

// "☕ Giving a break: NAME  ✓" — shown while a reliever covers the room.
// Blinks on the atcFlash clock, in sync with the amber pill. The green ✓
// completes the break (the caller wires it to endBreak/endMyBreak) and is the
// ONLY way a covered break ends on this screen, exactly like the web strip.
// canComplete=false renders the strip without the ✓ (a viewer who is neither
// the cover nor on the room sees state, not controls).
export function GivingStrip({ relieverName, onComplete, canComplete = true, busy = false }) {
  // ⚠️ THE CALLER MUST GATE THIS ON THE BREAK STATE, NOT ON THE NAME.
  // `relieverName` is written the moment a cover is NAMED, so an ungated render
  // showed "☕ Giving a break: X" — with a working ✓ that would have completed
  // a break nobody had started — about a room X had not walked into. See
  // `AwaitingCoverStrip` for the state that belongs there instead.
  if (!relieverName) return null;
  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: px(space.sm),
        marginTop: px(space.sm),
        padding: `${px(7)} ${px(10)}`,
        borderRadius: radius.md,
        border: `1.5px solid ${color.break.giving}`,
        background: "#fef8ec",
        color: "#7a4f00",
        fontSize: 12,
        fontWeight: 700,
        ...pulse("#f5e2b8"),
      }}
    >
      <span aria-hidden="true">☕</span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        Giving a break: <b>{relieverName}</b>
      </span>
      {canComplete && (
        <button
          type="button"
          onClick={onComplete}
          disabled={busy}
          aria-label="Mark break complete"
          style={{
            width: 34,
            height: 34,
            borderRadius: radius.full,
            border: `2px solid ${color.break.done}`,
            background: "#e6f8f3",
            color: "#0a7d5e",
            fontSize: 15,
            fontWeight: 900,
            cursor: busy ? "default" : "pointer",
            lineHeight: 1,
            flexShrink: 0,
            opacity: busy ? 0.5 : 1,
          }}
        >
          ✓
        </button>
      )}
    </div>
  );
}

// "⏳ Lunch break requested — waiting for coverage…" while a request is live.
// ⚠️ `onCancel` IS OPTIONAL AND THE BUTTON IS NOT DRAWN WITHOUT IT. Owner,
// 2026-09-11: "we have to find a way that the provider can cancel the break
// request himself." Withdrawing existed only on the web Timeline card, gated on
// an AIC, so the person who RAISED the request had no way back from their own
// phone — the strip pulsed at them until somebody else cleared it.
//
// The strip is also drawn where cancelling is not this viewer's to do (a
// colleague's room), and there a control that refuses is worse than no control
// at all — so the caller decides by passing the handler or not.
export function RequestStrip({ channel, onCancel = null, busy = false }) {
  if (!channel) return null;
  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: px(space.sm),
        marginTop: px(space.sm),
        padding: `${px(7)} ${px(10)}`,
        borderRadius: radius.md,
        border: `1.5px solid ${color.break.requested}`,
        background: "#3a0d0d",
        color: "#fecaca",
        fontSize: 12,
        fontWeight: 700,
        ...pulse("#240707"),
      }}
    >
      <span aria-hidden="true">⏳</span>
      {/* minWidth:0 so the message shrinks rather than pushing the button off a
          390px screen — on a phone the answer is a wrapped line, never a
          sideways drag. */}
      <span style={{ minWidth: 0, flex: 1 }}>
        {CHANNEL_LABELS[channel] || channel} break requested — waiting for coverage…
      </span>
      {onCancel ? (
        <button
          type="button"
          data-testid="cancel-break-request"
          onClick={onCancel}
          disabled={Boolean(busy)}
          // "Cancel", not "Withdraw" or "×": the control is named for its
          // consequence, and a bare glyph beside a pulsing red strip reads as
          // "dismiss this notice" rather than "call off the request".
          aria-label={`Cancel the ${CHANNEL_LABELS[channel] || channel} break request`}
          style={{
            flexShrink: 0,
            // 48, never 44 — engines floor to 1/64px, so a box budgeted at
            // exactly the audit floor renders 43.98 and fails it.
            minHeight: px(TOUCH_TARGET_MIN),
            padding: `0 ${px(12)}`,
            borderRadius: radius.md,
            border: `1px solid ${color.break.requested}`,
            background: "transparent",
            color: "#fecaca",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.04em",
            fontFamily: "inherit",
            opacity: busy ? 0.6 : 1,
          }}
        >
          CANCEL
        </button>
      ) : null}
    </div>
  );
}
